import { ref, readonly, onMounted, onUnmounted } from 'vue';
import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';

const NUM_PIXELS_BETWEEN_TOGGLE_BUTTON_AND_MENU = 2;
const MIN_MENU_HEIGHT_PIXELS = 100;
const MIN_PIXELS_BETWEEN_MENU_AND_WINDOW_EDGE = 10;
const DROPDOWN_MENU_BORDER_WIDTH_PIXELS = 1;

const htmlNode = document.body.parentNode;

export function useDropdownMenu({
  isCustomRightClickMenu,
  customRightClickMenuContainerNode,
  dropdownMenuToggleButtonNode,
  dropdownMenuNode,
  onClose,
  shouldBlurDropdownMenuToggleButtonNodeOnClose
}) {
  const isDropdownMenuOpen = ref(false);
  const shouldDropdownMenuRemainOpen = ref(false);
  let isDestroyed = false;

  function initializeDropdownMenu() {
    if (isCustomRightClickMenu) {
      customRightClickMenuContainerNode.value.addEventListener('contextmenu', openDropdownMenu);
      return;
    }

    dropdownMenuToggleButtonNode.value.addEventListener('click', openDropdownMenu);
    window.addEventListener('resize', resizeRegularDropdownMenu);
  }

  function openDropdownMenu(e) {
    if (
      isDestroyed ||
      (isCustomRightClickMenu && customRightClickMenuContainerNode.value.classList.contains('custom-right-click-dropdown-menu-disabled'))
    ) {
      return;
    }

    e.preventDefault();

    isDropdownMenuOpen.value = true;

    if (isCustomRightClickMenu) {
      resizeCustomRightClickDropdownMenu(e);
    } else {
      dropdownMenuToggleButtonNode.value.removeEventListener('click', openDropdownMenu);
      resizeRegularDropdownMenu();
    }

    window.setTimeout(() => {
      if (isDestroyed) {
        return;
      }

      htmlNode.addEventListener('click', handleHtmlNodeClickWrapperFunction);
    }, 0);
  }

  function handleHtmlNodeClickWrapperFunction() {
    if (shouldDropdownMenuRemainOpen.value || isDestroyed) {
      return;
    }

    window.setTimeout(handleHtmlNodeClick, 0);
  }

  function handleHtmlNodeClick() {
    if (shouldDropdownMenuRemainOpen.value || isDestroyed) {
      return;
    }

    isDropdownMenuOpen.value = false;
    htmlNode.removeEventListener('click', handleHtmlNodeClickWrapperFunction);

    if (typeof onClose === 'function') {
      onClose();
    }

    if (!isCustomRightClickMenu && shouldBlurDropdownMenuToggleButtonNodeOnClose) {
      dropdownMenuToggleButtonNode.value.blur();
    }

    window.setTimeout(() => {
      if (isDestroyed) {
        return;
      }

      if (!isCustomRightClickMenu) {
        dropdownMenuToggleButtonNode.value.addEventListener('click', openDropdownMenu);
      }
    }, 0);
  }

  async function resizeCustomRightClickDropdownMenu(e) {
    if (isDestroyed) {
      return;
    }

    dropdownMenuNode.value.style.opacity = '0.0';
    dropdownMenuNode.value.style.maxHeight = `${window.innerHeight - (DROPDOWN_MENU_BORDER_WIDTH_PIXELS * 2)}px`;
    dropdownMenuNode.value.style.maxWidth = `${window.innerWidth - (DROPDOWN_MENU_BORDER_WIDTH_PIXELS * 2)}px`;

    await waitForDomToRepaint();

    if (isDestroyed) {
      return;
    }

    dropdownMenuNode.value.style.opacity = null;
    let top = Math.min(e.clientY, window.innerHeight - dropdownMenuNode.value.clientHeight - DROPDOWN_MENU_BORDER_WIDTH_PIXELS);
    top += htmlNode.scrollTop;
    const left = Math.min(e.clientX, window.innerWidth - dropdownMenuNode.value.clientWidth - DROPDOWN_MENU_BORDER_WIDTH_PIXELS);
    dropdownMenuNode.value.style.top = `${top}px`;
    dropdownMenuNode.value.style.left = `${left}px`;
  }

  function resizeRegularDropdownMenu() {
    if (isDestroyed) {
      return;
    }

    const buttonBCR = dropdownMenuToggleButtonNode.value.getBoundingClientRect();
    const containerBCR = dropdownMenuToggleButtonNode.value.parentElement.getBoundingClientRect();
    let maxHeight;

    if (dropdownMenuNode.value.classList.contains('dropdown-menu-bottom-aligned')) {
      const bottom = (containerBCR.bottom - buttonBCR.bottom) + buttonBCR.height + NUM_PIXELS_BETWEEN_TOGGLE_BUTTON_AND_MENU;
      dropdownMenuNode.value.style.bottom = `${bottom}px`;
      maxHeight = window.innerHeight - ((window.innerHeight - buttonBCR.top) - NUM_PIXELS_BETWEEN_TOGGLE_BUTTON_AND_MENU) - MIN_PIXELS_BETWEEN_MENU_AND_WINDOW_EDGE;
      maxHeight = Math.max(MIN_MENU_HEIGHT_PIXELS, maxHeight);
      dropdownMenuNode.value.style.maxHeight = `${maxHeight}px`;
      return;
    }

    const top = (buttonBCR.top - containerBCR.top) + buttonBCR.height + NUM_PIXELS_BETWEEN_TOGGLE_BUTTON_AND_MENU;
    dropdownMenuNode.value.style.top = `${top}px`;
    maxHeight = window.innerHeight - (buttonBCR.bottom + NUM_PIXELS_BETWEEN_TOGGLE_BUTTON_AND_MENU) - MIN_PIXELS_BETWEEN_MENU_AND_WINDOW_EDGE;
    maxHeight = Math.max(MIN_MENU_HEIGHT_PIXELS, maxHeight);
    dropdownMenuNode.value.style.maxHeight = `${maxHeight}px`;
  }

  function keepDropdownMenuOpen() {
    shouldDropdownMenuRemainOpen.value = true;

    window.setTimeout(() => {
      shouldDropdownMenuRemainOpen.value = false;
    }, 0);
  }

  function destroyDropdownMenu() {
    isDestroyed = true;

    htmlNode.removeEventListener('click', handleHtmlNodeClickWrapperFunction);

    if (isCustomRightClickMenu) {
      customRightClickMenuContainerNode.value.removeEventListener('contextmenu', openDropdownMenu);
      return;
    }

    dropdownMenuToggleButtonNode.value.removeEventListener('click', openDropdownMenu);
    window.removeEventListener('resize', resizeRegularDropdownMenu);
  }

  onMounted(initializeDropdownMenu);
  onUnmounted(destroyDropdownMenu);

  return {
    isDropdownMenuOpen: readonly(isDropdownMenuOpen),
    keepDropdownMenuOpen
  };
}
